/**
 * Taking the level's items (level-setup.s D_2CB7, once per main-loop pass at $0A54) and the 35 power-up
 * effects, dispatched as the C64 does through D_2D65 / D_2D88 (special-item-effects.s). Each case names
 * the routine it ports.
 */

import { endRound } from '../flow';
import { addScore } from '../score';
import { announce, pause } from './act';
import { takeBlast, takeBlueCross } from './blast';
import { takePotion } from './bonus';
import { emptySlot, slotUnits } from './slots';
import { takeYellowCross } from './strike';
import { CLOCK_PASSES, PICKUP_RANGE, RED_CROSS_BOLTS, SKIP_BLUE, SKIP_FLASH_FRAMES, SKIP_PURPLE, WARP_TO } from './tuning';
import type { ItemSystem, Slot } from './state';
import type { LevelExit, PlayerId, World } from '../types';

/** The staffs and boxes: their giant item drops at the clear (D_348A..D_350A store $68/$69/$6A). */
const GIANTS: readonly number[] = [1, 2, 21, 23, 25, 26, 27];
/** No effect: D_3620 (green crystal ball, purple crystal ball, bell) and D_35A7 (green lamp) are an rts. */
export const NO_EFFECT: readonly number[] = [19, 28, 30, 31];

/** D_7FA4 / D_7FA7 / D_7C37 into D_7FB2: the border flashes, then the levels scroll on to the target. */
function scrollOn(world: World, sys: ItemSystem, exit: LevelExit): void {
  endRound(world, exit, SKIP_FLASH_FRAMES);
  pause(world, sys, SKIP_FLASH_FRAMES);
  announce(world, 'skip');
}

/** D_34A0: every player in the game (not dying) goes to state $18 - twice as fast, flashing, immune. */
function takeHeart(world: World, sys: ItemSystem, id: PlayerId): void {
  world.power.heart = id;
  for (const pid of [0, 1] as const) if (world.players[pid].state === 'play') sys.heartOn[pid] = true;
  announce(world, 'heart');
}

/** The effect of power-up `index` for player `id` (D_2D40: `jsr (D_2D65/D_2D88),y`). */
export function applyPowerup(world: World, sys: ItemSystem, index: number, id: PlayerId): void {
  const m = world.players[id].motion;
  const candies = (): void => {
    m.yellowCandy = true;
    m.purpleCandy = true;
    m.blueCandy = true;
  };
  if (GIANTS.includes(index)) {
    world.power.giant = index;
    return;
  }
  switch (index) {
    case 0: m.shoe = true; return; // final_routine: D_8728 = $FE
    case 3: m.yellowCandy = true; return; // D_2DAB: fire cooldown 3
    case 4: m.purpleCandy = true; return; // D_2DB2: range $8C
    case 5: m.blueCandy = true; return; // D_2DCD: D_A781
    case 6: world.power.redCross[id] = RED_CROSS_BOLTS; return; // D_A64D: D_A783 = $12
    case 7: takeYellowCross(world, sys, id); return; // D_A653
    case 8: takeBlueCross(world, sys, id); return; // D_2DD7
    case 9: // D_2DBE: the three rings, then D_2DC7's candies
      m.purpleRing = true;
      m.redRing = true;
      m.blueRing = true;
      candies();
      return;
    case 10: candies(); return; // D_2DC7
    case 11: takeBlast(world, sys, 'lamp', id); return; // D_2ECC
    case 12: m.purpleRing = true; return; // D_2F5F: dec $61,x
    case 13: m.redRing = true; return; // D_2F62: dec $63,x
    case 14: m.blueRing = true; return; // D_2F65: dec $65,x
    case 15: // $2F68: the background colours flip, SESSION = $87, TXTTAB stopped
      world.power.clock = CLOCK_PASSES;
      announce(world, 'clock');
      return;
    case 16: case 17: case 18: takePotion(world, sys, index); return; // D_2F7D / D_2F83 / D_2F89
    case 20: scrollOn(world, sys, { kind: 'skip', to: Math.min(100, world.level.n + SKIP_BLUE) }); return;
    case 22: scrollOn(world, sys, { kind: 'skip', to: Math.min(100, world.level.n + SKIP_PURPLE) }); return;
    case 24: takeHeart(world, sys, id); return;
    case 29: takeBlast(world, sys, 'bomb', id); return; // D_35B0
    case 32: endRound(world, { kind: 'secret', from: world.level.n }, 1); return; // D_3621: at once
    case 33: scrollOn(world, sys, { kind: 'warp', to: WARP_TO }); return; // D_7C37: level index $45
    case 34: world.power.lightning[id] = true; return; // D_7C3C: dec D_37C7,x
    default: return; // 19, 28, 30, 31: no effect, faithfully
  }
}

/** A player takes a slot's item: its points (D_2D2D), then for the power-up its effect. */
function take(world: World, sys: ItemSystem, slot: Slot, kind: 'food' | 'powerup', id: PlayerId): void {
  // Level 100's food slot holds a lightning potion too (index $51 reads the power-up tables' entry 34).
  const as = world.level.n >= 100 ? 'powerup' : kind;
  const units = slotUnits(slot, as);
  const item = slot.item;
  const itemId = slot.itemId;
  emptySlot(world, slot);
  addScore(world, id, units);
  if (as === 'food') {
    world.events.push({ type: 'collect', player: id, itemId, value: units });
    return;
  }
  world.events.push({ type: 'powerup', player: id, item });
  applyPowerup(world, sys, item, id);
}

/**
 * D_2CB7: player 2 then 1, not dying or dead (state $0E / $0F); the power-up slot, then the food; a
 * shown item within 16 px on both axes is taken. (The C64 marks it taken and removes it on the next
 * pass, so two players on it in one pass both score; here the first takes it.)
 */
export function pickups(world: World, sys: ItemSystem): void {
  const f = world.flow;
  if (f.phase !== 'play' && f.phase !== 'clear') return;
  for (const id of [1, 0] as const) {
    const p = world.players[id];
    if (p.state !== 'play') continue;
    for (const [slot, kind] of [[sys.powerup, 'powerup'], [sys.food, 'food']] as const) {
      if (!slot.visible || sys.bonus) continue;
      if (Math.abs(p.x - slot.x) >= PICKUP_RANGE || Math.abs(p.y - slot.y) >= PICKUP_RANGE) continue;
      take(world, sys, slot, kind, id);
    }
  }
}
